/**
 * This helper allows us to connect the current user to a third-party auth
 * provider (e.g. Google, Facebook)
 *
 * @see
 * https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/650773166/Sign+in+with+Google#2.-Re-authentication
 * https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/658276814/Sign+in+with+Facebook#2.-Re-authentication
 */
import gql from "graphql-tag";

import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError, logUnreachableCode} from "@khan/logging";

import type {LoginType} from "../shared/types.ts";

import type {
    linkThirdPartyAccountMutationType,
    reauthWithThirdPartyMutationType,
} from "./__graphql-types__/index.ts";

export const THIRD_PARTY_REAUTH_MUTATION: GqlOperation<
    reauthWithThirdPartyMutationType["response"],
    reauthWithThirdPartyMutationType["variables"]
> = gqlOp(gql`
    mutation reauthWithThirdPartyMutation(
        $loginType: String!
        $token: String!
    ) {
        reauthWithThirdParty(loginType: $loginType, token: $token) {
            error {
                code
            }
            user {
                id
                kaid
            }
        }
    }
`);

/**
 * Re-authenticate the current user with a third-party provider.
 *
 * @param {LoginType} loginType The third-party provider to re-authenticate
 * with.
 * @param {string} token The user's access token to connect the account.
 * @returns {Promise<ThirdPartyResult} Whether we return an error or the
 * user data.
 */
export const reauthWithThirdParty = async (
    gqlClient: TSFixMe<"TODO(FEI-6897): Requires an Apollo client, should be switched to use a gql client.">,
    loginType: LoginType,
    token: string,
) => {
    try {
        const result = await gqlClient.mutate(THIRD_PARTY_REAUTH_MUTATION, {
            variables: {loginType, token},
        });

        if (result.data?.reauthWithThirdParty?.error) {
            throw new KAError(
                result.data.reauthWithThirdParty.error.code,
                Errors.Internal,
            );
        }

        const user = result.data?.reauthWithThirdParty?.user;

        // User has been re-authenticated!
        if (user) {
            return {user};
        }

        return;
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError) {
            throw error;
        }

        // Otherwise we throw a generic error.
        throw new KAError("Failed to re-auth", Errors.Internal);
    }
};

export const LINK_THIRD_PARTY_ACCOUNT_MUTATION: GqlOperation<
    linkThirdPartyAccountMutationType["response"],
    linkThirdPartyAccountMutationType["variables"]
> = gqlOp(gql`
    mutation linkThirdPartyAccountMutation(
        $loginType: String!
        $token: String!
    ) {
        linkThirdPartyAccount(loginType: $loginType, token: $token) {
            error {
                code
            }
            user {
                id
                kaid
            }
        }
    }
`);

/**
 * Links a new third-party account with the current user.
 *
 * @param {LoginType} loginType The third-party provider to add.
 * @param {string} token The user's access token to connect the account.
 * @returns {Promise<ThirdPartyResult} Whether we return an error or the
 * user data.
 */
export const linkThirdPartyAccount = async (
    gqlClient: TSFixMe<"TODO(FEI-6897): Requires an Apollo client, should be switched to use a gql client.">,
    loginType: LoginType,
    token: string,
) => {
    try {
        const result = await gqlClient.mutate(
            LINK_THIRD_PARTY_ACCOUNT_MUTATION,
            {variables: {loginType, token}},
        );

        if (
            result.data &&
            result.data.linkThirdPartyAccount &&
            result.data.linkThirdPartyAccount.error
        ) {
            throw new KAError(
                result.data.linkThirdPartyAccount.error.code,
                Errors.Internal,
            );
        }

        const user = result.data?.linkThirdPartyAccount?.user;

        // User has been re-authenticated!
        if (user) {
            return {user};
        }

        return;
    } catch (error: any) {
        // We handle the exception thrown from within the mutation success
        // handler.
        if (error instanceof KAError) {
            throw error;
        }

        // Otherwise we throw a generic error.
        throw new KAError(
            "Failed to link a third-party account: " + loginType,
            Errors.Internal,
        );
    }
};

/**
 * Get the message key depending on the third-party provider used by the user.
 *
 * @param {LoginType} loginType The third-party provider added to the account.
 * @returns {string} The key that is going to be used as a query string to
 * present the correct message.
 */
const getConnectedMessageKey = (loginType: LoginType) => {
    switch (loginType) {
        case "google":
            return "you_can_now_login_with_google";
        case "facebook":
            return "you_can_now_login_with_facebook";
        case "apple":
            return "you_can_now_login_with_apple";
        case "clever":
            return "you_can_now_login_with_clever";
        case "clever_library":
            return "you_can_now_login_with_clever_library";
        case "microsoft":
            return "you_can_now_login_with_microsoft";
        // Not used, but adding it to make TypeScript happy.
        case "clever_district":
        case "classlink":
        case "password":
            return "N/A";
        default:
            throw logUnreachableCode(
                "Unsupported third party login type",
                loginType,
            );
    }
};

/**
 * Map the error code to a message key that can be processed by the UI.
 *
 * @returns {string} The key that is going to be used as a query string to
 * present the correct message.
 *
 * @see apps/khanacademy/src/settings-package/settings-page.jsx#MESSAGE_KEY_MAPPING
 */
const getErrorMessageKey = (errorCode: string, loginType?: LoginType) => {
    switch (errorCode) {
        case "ACCOUNT_ALREADY_LINKED":
            if (loginType === "apple") {
                return "already_linked_an_apple_account";
            }
            return "account_already_linked";
        case "ALREADY_LINKED_A_FACEBOOK_ACCOUNT":
            return "already_linked_a_facebook_account";
        case "ALREADY_LINKED_A_GOOGLE_ACCOUNT":
            return "already_linked_a_google_account";
        case "DIDNT_LOGIN_SUCCESSFULLY":
            return "didnt_login_successfully";
        case "NO_EMAIL_ASSOCIATED_WITH_FACEBOOK_ACCOUNT":
            return "no_email_associated_with_facebook_account";
        case "REAUTH_REQUIRED":
            return "reauth_required";
        case "NOT_LOGGED_IN":
            return "didnt_login_successfully";
        default:
            return "link_account_error";
    }
};

/**
 * Build the reauth URL where the user is going to be redirected to.
 *
 * This URL is built depending if the account is added or if we get an error
 * from the query.
 * @param {LoginType} loginType The third-party provider identifier.
 * @param {string} error The error code.
 */
export const createReauthUrl = ({
    loginType,
    pathname = "/settings/account",
    error,
}: {
    loginType: LoginType;
    pathname?: string;
    error?: string | null;
}): string => {
    const messageKey = error
        ? // query error
          getErrorMessageKey(error, loginType)
        : // success
          getConnectedMessageKey(loginType);
    const search = new URLSearchParams({messageKey});

    return `${pathname}?${search.toString()}`;
};
